import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SearchIcon } from '../../../assets/icons/search-icon.svg';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  AccountLevelInterface,
  RequestAccountLevelInterface,
} from '../../../interface/account-level.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchAccountsLevel,
  fetchSettings,
  setSortData,
  refreshAccountsLevel,
} from '../../../redux/slices/AccountLevelSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';

import SidePanelAddFee from './addFee';
import SidePanelAccountDetails from './SidePanelAccountDetails';

/**
 * Account level component
 * @returns {any} JSX Element for the AccountLevel component
 */
function AccountLevel() {
  const branding = useAppBranding();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('sharedComponent');

  const {
    accountLevel,
    // settings,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.accountLevel);

  const [accountLevelData, setAccountLevelData] = useState<AccountLevelInterface[]>([]);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<AccountLevelInterface | null>(null);
  const [sidePanelMode, setSidePanelMode] = useState<'add' | 'view' | 'edit'>('add');
  const [searchValue, setSearchValue] = useState('');

  /** ----------------- Columns ----------------- */
  const columns = [
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof AccountLevelInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof AccountLevelInterface,
      sortAllow: true,
    },
    {
      label: t('platformFee'),
      accessorKey: 'platformFee' as keyof AccountLevelInterface,
      sortAllow: true,
      /**
       * Rendering platform fee cells
       * @param {any} currentRow - AccountLevelInterface object containing fee data
       * @returns {any} JSX Element representing the fee
       */
      customData: (currentRow: AccountLevelInterface) => (
        <div className="fee-price-container value-container">
          <p className="value">{currentRow?.platformFee ?? '-'}</p>
          <span>
            {currentRow?.platformFee !== null &&
              currentRow?.platformFee !== undefined &&
              currentRow?.platformFee !== '' &&
              (currentRow?.platformFeeType === 'amount' ? branding.text.currency : '%')}
          </span>
        </div>
      ),
    },
    {
      label: t('sessionFee'),
      accessorKey: 'sessionFee' as keyof AccountLevelInterface,
      sortAllow: true,
      /**
       * Rendering session fee cells
       * @param {any} currentRow - AccountLevelInterface object containing fee data
       * @returns {any} JSX Element representing the fee
       */
      customData: (currentRow: AccountLevelInterface) => (
        <div className="fee-price-container value-container">
          <p className="value">{currentRow?.sessionFee ?? '-'}</p>
          <span>
            {currentRow?.sessionFee !== null &&
              currentRow?.sessionFee !== undefined &&
              currentRow?.sessionFee !== '' &&
              (currentRow?.sessionFeeType === 'amount' ? branding.text.currency : '%')}
          </span>
        </div>
      ),
    },
  ];

  /**
   * ----------------- Data fetch -----------------
   * @param {any} data - The data
   */
  const fetchData = (data: RequestAccountLevelInterface) => {
    if (!loading) dispatch(fetchAccountsLevel(data));
  };
  /**
   * Handle the add fees function
   */
  const handleAddFees = () => {
    setSelectedRow(null);
    setSidePanelMode('add');
    setSidePanelOpen(true);
  };
  /**
   * Handle the view fees function
   * @param {any} row - Selected account level item to view
   */
  const handleViewFees = (row: AccountLevelInterface) => {
    setSelectedRow(row);
    setSidePanelMode('view');
    setSidePanelOpen(true);
  };
  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function for page and limit change
   * @param {any} value - updated limit and page data
   */
  const pageAndLimitChange = (value: RequestAccountLevelInterface) => {
    const query = GetParametersFromCommonFilters(filters);
    fetchData({ ...query, ...value, sortBy, sortField });
  };

  /**
   * Function for search filter
   * @param {any} name - The field name being searched
   * @param {any} value - Search string values
   */
  const handleSearch = (name: string, value: string) => {
    setSearchValue(value);
    fetchData({ searchBy: value, page: DefaultApiLimit.page, limit });
  };

  /**
   * Handle the auto refresh function
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshAccountsLevel({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /** ----------------- Effects ----------------- */
  useEffect(() => {
    dispatch(setPageTitle(`${t('feeConfiguration')} (${totalResults})`));
  }, [totalResults]);
  useEffect(() => {
    if (accountLevel?.length) setAccountLevelData(accountLevel);
  }, [accountLevel]);

  useEffect(() => {
    fetchData({ page, limit });
    dispatch(fetchSettings());
  }, []);

  return (
    <div className="account-level-wrap" dir={i18n.dir()}>
      <div className="account-level-header">
        <div className="search-input">
          <CustomInput
            placeholder={t('Search here...')}
            name="searchBy"
            svgIcon={<SearchIcon />}
            value={searchValue}
            onChangeValue={(_, value) => handleSearch('searchBy', String(value))}
            type="text"
          />
        </div>

        {accountLevelData?.[0]?.accountCustomId === 'kathir' && (
          <div>
            <MyButton
              label={t('addOverride')}
              buttonType="submit"
              onClickFunc={handleAddFees}
              disableButton={!permissionCheck('mobilityAccountLevelAdd')}
            />
          </div>
        )}
      </div>
      <CustomTable
        header={columns}
        data={accountLevelData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        version="v2"
        sidePanelEnabled={sidePanelOpen}
        rowClickEvent={(id, row) => {
          handleViewFees(row);
        }}
        height={510}
        rowClickParamsKey="accountCustomId"
        sidePanelManualContent={
          sidePanelMode === 'add' || sidePanelMode === 'edit' ? (
            <SidePanelAddFee
              onClose={() => setSidePanelOpen(false)}
              onRefresh={() => fetchData({ page, limit })}
              data={sidePanelMode === 'edit' ? selectedRow : null}
              mode={sidePanelMode}
            />
          ) : (
            <SidePanelAccountDetails
              data={selectedRow}
              onClose={() => setSidePanelOpen(false)}
              onEdit={() => {
                setSidePanelOpen(false);
                setTimeout(() => {
                  setSidePanelMode('edit');
                  setSidePanelOpen(true);
                }, 100);
              }}
            />
          )
        }
        onSidePanelClose={() => {
          setSidePanelOpen(false);
        }}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
      />
    </div>
  );
}

export default AccountLevel;
